import { useUpdateTransferRequest } from "@features/cx/transfer/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Checkbox } from "@shared/ui/checkbox";
import {
  ArrowRightLeft,
  Building,
  CheckCircle,
  CheckSquare,
  Code,
  DollarSign,
  Download,
  Eye,
  FileText,
  Home,
  Info,
  MapPin,
  Paperclip,
  X,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TransferRequest } from "../../../types";
import { useUpdateConsumerDocument } from "@features/cx/transfer/hooks";
import { cn } from "@shared/lib/utils";
import { logEvent } from "@shared/analytics/analytics";

interface ClosureRequirements {
  customerConfirmation: boolean;
  qualityAssuranceDone: boolean;
}

interface TransferDetailsTabProps {
  request: TransferRequest;
  onResolve?: (requestId: string, payload: any) => void;
  onReject?: (requestId: string) => void;
  onRejectConfirm?: (reason: string) => void;
}

interface Requirement {
  label: string;
  status: boolean;
}

const mockData = {
  code: "TR-OWNERSHIP-001",
  transferType: "Ownership Transfer",
  changeBillingAddress: "Yes",
  description:
    "Transfer of account ownership between family members due to marriage or relationship changes",
  slaTarget: {
    acknowledgement: "2 hours",
    response: "24 hours",
    resolution: "5 business days",
  },
  slaAchieved: {
    acknowledgement: "1.5 hours",
    response: "18 hours",
    resolution: "3 business days",
  },
  newAccountHolder: {
    name: "Sarah Johnson",
    phone: "(212) 555-5678",
    email: "sarah.johnson@example.com",
    relation: "Spouse",
  },
  accountBalance: {
    current: "$245.78",
    outstanding: "$0.00",
    lastPaymentDate: "2025-05-10",
    lastPaymentAmount: "$150.00",
    responsibility: "Transfer outstanding balance",
  },
  uploadedDocuments: [
    {
      name: "ID Verification.pdf",
      type: "Identity",
      date: "2025-05-18",
      status: "Verified",
    },
    {
      name: "Proof of Address.pdf",
      type: "Address",
      date: "2025-05-18",
      status: "Pending",
    },
    {
      name: "Transfer Authorization.pdf",
      type: "Authorization",
      date: "2025-05-18",
      status: "Verified",
    },
  ],
  specialInstructions: "Expedite process due to travel plans",
};
const statusColor = (status: string) => {
  switch (status) {
    case "Verified":
    case "Completed":
      return "text-green-600";
    case "Pending":
      return "text-blue-600";
    default:
      return "text-gray-600";
  }
};

// Utility to format date as 'YYYY-MM-DD HH:mm:ss.SSS +0530' (IST)
function formatDateIST(date = new Date()) {
  const offset = 330; // IST offset in minutes
  const pad = (n, z = 2) => ("00" + n).slice(-z);
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  const ist = new Date(utc + offset * 60000);
  const yyyy = ist.getFullYear();
  const mm = pad(ist.getMonth() + 1);
  const dd = pad(ist.getDate());
  const hh = pad(ist.getHours());
  const min = pad(ist.getMinutes());
  const ss = pad(ist.getSeconds());
  const ms = pad(ist.getMilliseconds(), 3);
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${ms} +0530`;
}

const TransferDetailsTab: React.FC<TransferDetailsTabProps> = ({
  request,
  onResolve,
  onReject,
}) => {
  console.log("Request1", request);
  const { id } = useParams<{ id: string }>();
  const { mutateAsync: updateServiceRequest } = useUpdateTransferRequest(
    Number(id)
  );
  const updateConsumerDocument = useUpdateConsumerDocument();
  const [requirements, setRequirements] = useState<Requirement[]>([
    { label: "Proof of Identity", status: false },
    { label: "Proof of Address", status: false },
    { label: "Transfer Authorization", status: false },
    { label: "Financial Verification", status: false },
  ]);
  const handleCheckboxChange = (label: string, checked: boolean) => {
    setCheckboxValues((prev) => ({
      ...prev,
      [label]: checked,
    }));
  };
  const [checkboxValues, setCheckboxValues] = useState<Record<string, boolean>>(
    {}
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (request?.additionalData?.closureRequirements) {
      setCheckboxValues({
        "Proof of Identity":
          request.additionalData.closureRequirements.proofOfIdentity,
        "Proof of Address":
          request.additionalData.closureRequirements.proofOfAddress,
        "Transfer Authorization":
          request.additionalData.closureRequirements.transferAuthorization,
        "Financial Verification":
          request.additionalData.closureRequirements.financialVerification,
      });
    }
  }, [request]);
  const [documentStatuses, setDocumentStatuses] = useState<
    Record<number, number>
  >({});
  const currency = useCurrency();
  const handleResolveRequest = async () => {
    // Convert to snake_case
    const additionalData = Object.entries(checkboxValues).reduce(
      (acc, [label, isChecked]) => {
        const snakeCaseLabel = label.toLowerCase().replace(/\s+/g, "_");
        acc[snakeCaseLabel] = isChecked;
        return acc;
      },
      {} as Record<string, boolean>
    );
    const closeDate = formatDateIST();
    const payload = {
      status: 2,
      close_date: closeDate,
      additional_data: {
        closure_requirements: additionalData,
      },
    };
    updateServiceRequest(payload, {
      onSuccess: () => {
        logEvent('Transfer Approved', {
          id,
        })
      }
    });
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.transferRequests.details(id),
    });
    navigate("/cx/transfer"); // Redirect after approve
  };

  const handleRejectRequest = async () => {
    if (onReject) {
      onReject(request.id.toString());
    }
    await queryClient.invalidateQueries({
      queryKey: QueryKeyFactory.module.cx.transferRequests.details(id),
    });
    navigate("/cx/transfer"); // Redirect after reject
  };

  console.log("Status Display", request.statusDisplay);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Request Details
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4 p-4 border border-gray-200 rounded-md mt-2">
            <h4 className="font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Basic Information
            </h4>
            <div className="grid grid-cols-2 font-semibold text-black md:grid-cols-4 pl-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Code size={12} />
                  Code
                </p>
                <p className="text-sm font-medium">
                  {request?.utilitySupportRequest?.configurationCode}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <ArrowRightLeft size={12} />
                  Transfer Type
                </p>
                <p className="text-sm font-medium">
                  {request?.utilitySupportRequest?.name}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin size={12} />
                  Change Billing Address
                </p>
                <p className="text-sm font-medium">
                  {request?.additionalData?.changeBillingAddress ? "Yes" : "No"}
                </p>
              </div>
              {request?.additionalData?.changeBillingAddress === true && (
                <div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Home size={12} />
                    Billing Address
                  </p>
                  <p className="text-sm font-medium">
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[0]?.territoryCode
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[1]?.territoryCode
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[3]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[4]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[6]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[7]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[8]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[9]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[10]?.territoryName
                    }
                    ,
                    {
                      request?.additionalData?.newConsumerData
                        ?.billingAddressData[2]?.territoryCode
                    }
                  </p>
                </div>
              )}
              <div className="col-span-2 md:col-span-4">
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <FileText size={12} />
                  Description
                </p>
                <p className="text-sm font-medium">
                  {request?.utilitySupportRequest?.longDescription}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-md">
              {/* SLA Target */}
              <div className="border border-gray-200 rounded-md p-4">
                <p className="text-sm font-semibold mb-2">SLA Target</p>
                <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                  <div>
                    <p className="mb-1">Acknowledgement</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.acknowledgementTime ?? "-"}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1">Response</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.responseTime ?? "-"}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1">Resolution</p>
                    <p className="text-foreground font-medium">
                      {request?.utilitySupportRequest?.extraData
                        ?.resolutionTime ?? "-"}
                    </p>
                  </div>
                </div>
              </div>

              {/* SLA Achieved */}
              <div className="border border-gray-200 rounded-md p-4">
                <p className="text-sm font-semibold mb-2">SLA Achieved</p>
                <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                  <div>
                    <p className="mb-1">Acknowledgement</p>
                    <p className="text-green-600 font-medium">
                      {request?.acknowledgementTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1">Response</p>
                    <p className="text-green-600 font-medium">
                      {request?.responseTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                  <div>
                    <p className="mb-1">Resolution</p>
                    <p className="text-green-600 font-medium">
                      {request?.resolutionTimeAchieved ?? "Pending"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New Account Holder & Account Balance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* New Account Holder Information */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-md">
              <h4 className="font-medium flex items-center gap-2">
                <Building className="h-4 w-4" />
                New Account Holder Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Name</p>
                  <p className="text-sm font-medium">
                    {request?.additionalData?.newConsumerData?.firstName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Phone Number</p>
                  <p className="text-sm font-medium">
                    {request?.additionalData?.newConsumerData?.contactNumber}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Email</p>
                  <p className="text-sm font-medium">
                    {request?.additionalData?.newConsumerData?.email}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Relation to Current Account
                  </p>
                  <p className="text-sm font-medium">
                    {request?.relation ?? "NA"}
                  </p>
                </div>
              </div>
            </div>
            {/* Account Balance - Financial Details */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-md">
              <h4 className="font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Account Balance - Financial Details
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Bill Month</p>
                  <p className="text-sm font-medium">
                    {request?.financialDetails?.billMonth || "NA"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Outstanding Amount
                  </p>
                  <p className="text-sm font-medium">
                    {currency}{" "}
                    {request?.financialDetails?.outstandingBalance || "NA"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Last Payment Date
                  </p>
                  <p className="text-sm font-medium">
                    {request?.financialDetails?.lastPaymentDate ?? "NA"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">
                    Last Payment Amount
                  </p>
                  <p className="text-sm font-medium">
                    {request?.financialDetails?.lastPayAmount ?? "NA"}
                  </p>
                </div>
              </div>
              <div className="mt-2">
                <p className="text-xs text-muted-foreground">
                  Financial Responsibility
                </p>
                <p className="text-sm font-medium">
                  {request?.additionalData?.transferOutstandingBalance
                    ? "Transfer outstanding balance"
                    : "Clear balance before transfer"}
                </p>
              </div>
            </div>
          </div>

          {/* Uploaded Documents & Requirements Checklist */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Uploaded Documents */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-md">
              <h4 className="font-medium flex items-center gap-2">
                <Paperclip className="h-4 w-4" />
                Uploaded Documents
              </h4>
              <div className="space-y-2">
                {request?.additionalData?.newConsumerData?.document &&
                request.additionalData.newConsumerData.document.length > 0 ? (
                  request.additionalData.newConsumerData.document.map(
                    (doc, idx) => (
                      <div
                        key={doc.id || idx}
                        className="flex items-center justify-between bg-white border rounded px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <div>
                            <div className="font-medium text-sm">
                              {doc.file
                                ? doc.file.split("/").pop()
                                : "Document"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {doc.document_type || ""}{" "}
                              {doc.document_subtype
                                ? `• ${doc.document_subtype}`
                                : ""}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "text-xs font-semibold rounded px-2 py-0.5",
                              doc.status === 0
                                ? "text-gray-700 bg-gray-100"
                                : doc.status === 1
                                ? "text-green-700 bg-green-100"
                                : doc.status === 2
                                ? "text-red-700 bg-red-100"
                                : "text-muted-foreground"
                            )}
                          >
                            {doc.statusDisplay}
                          </span>

                          <Button size="icon" variant="ghost" >
                            <a
                              href={doc.file}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Eye className="h-4 w-4" />
                            </a>
                          </Button>

                          {doc.status === 0 && (
                            <>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={async () => {
                                  await updateConsumerDocument.mutateAsync({
                                    requestId: request.id,
                                    documentId: doc.id,
                                    documentStatus: 1,
                                  });
                                  await queryClient.invalidateQueries({
                                    queryKey:
                                      QueryKeyFactory.module.cx.transferRequests.details(
                                        id
                                      ),
                                  });
                                }}
                                title="Verify"
                              >
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={async () => {
                                  await updateConsumerDocument.mutateAsync({
                                    requestId: request.id,
                                    documentId: doc.id,
                                    documentStatus: 2,
                                  });
                                  await queryClient.invalidateQueries({
                                    queryKey:
                                      QueryKeyFactory.module.cx.transferRequests.details(
                                        id
                                      ),
                                  });
                                }}
                                title="Reject"
                              >
                                <X className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No documents uploaded
                  </div>
                )}
              </div>
            </div>
            {/* Requirements Checklist */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-md">
              <h4 className="font-medium flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Requirements Checklist
              </h4>
              <div className="space-y-2">
                {requirements.map((req, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between bg-white border rounded px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={checkboxValues[req.label] || false}
                        // disabled={
                        //   !(
                        //     request.statusDisplay === "PENDING" ||
                        //     request.statusDisplay === "IN_PROGRESS"
                        //   )
                        // }
                        onCheckedChange={(checked) =>
                          handleCheckboxChange(req.label, checked as boolean)
                        }
                      />
                      <span className="text-sm">{req.label}</span>
                    </div>
                    <span
                      className={`text-xs font-semibold ${
                        req.status ? "text-green-600" : "text-blue-600"
                      }`}
                    ></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Special Instructions */}
          <div className="space-y-4 p-4 border border-gray-200 rounded-md">
            <h4 className="font-medium flex items-center gap-2">
              <Info className="h-4 w-4" />
              Special Instructions
            </h4>
            <input
              className="w-full border rounded px-3 py-2 text-sm bg-white"
              value={request?.additionalData?.specialInstructions}
              readOnly
            />
          </div>

          {/* Approve/Reject Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            {(request.statusDisplay === "PENDING" ||
              request.statusDisplay === "ON HOLD" ||
              request.statusDisplay === "IN PROGRESS") && (
              <Button variant="destructive" onClick={handleRejectRequest}>
                <X className="h-4 w-4 mr-2" />
                Reject Request
              </Button>
            )}
            {(request.statusDisplay === "PENDING" ||
              request.statusDisplay === "ON HOLD" ||
              request.statusDisplay === "IN PROGRESS") && (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleResolveRequest}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approve Transfer
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransferDetailsTab;
